import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
import type {
  QueryFunctionContext,
  UseQueryOptions,
  UseQueryReturnType,
} from '@tanstack/vue-query';
import type {
  ColumnFiltersState,
  ColumnSizingState,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableOptions,
  Table as TanstackTable,
  VisibilityState,
} from '@tanstack/vue-table';
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import { computed, ref, shallowRef, toValue, watch } from 'vue';
import type { MaybeRef, Ref } from 'vue';
import { useRouter } from 'vue-router';

import type { PaginatedQueryParams } from '@stuut/api/query';
import { indexOf } from '@stuut/common/src/utils';
import { valueUpdater } from '@ui/utils/utils';

export interface UseTableOptions<T, Q extends PaginatedQueryParams = any> {
  defaultSort?: SortingState;
  forceRows?: MaybeRef<T[]>;
  onQueryChange?: () => any;
  params?: MaybeRef<Q>;
  persistentSelection?: T[];
  query?: UseQueryReturnType<T[], any>;
  queryOptions?: UseQueryOptions & {
    queryFn: (ctx: QueryFunctionContext) => Promise<T[]>;
  };
  selectItemsWithIds?: (id: string[]) => void;
  tableOptions: Omit<
    TableOptions<T>,
    'data' | 'getCoreRowModel' | 'manualFiltering' | 'manualPagination' | 'manualSorting' | 'state'
  >;
  useRouterQuery?: boolean;
}

export interface UseTableReturnType<T> {
  removeRow: (row: Row<T>) => void;
  rows: Ref<T[]>;
  table: TanstackTable<T>;
  updateRow: (row: Row<T>, update: T) => void;
}

export function usePaginatedTable<T, Q extends PaginatedQueryParams = any>(
  options: UseTableOptions<T, Q>,
): UseTableReturnType<T> {
  const router = useRouter();
  // const route = useRoute();
  const table = ref<TanstackTable<T>>();

  const queryOptions = ref(toValue(options.queryOptions));

  const queryClient = useQueryClient();

  const rows = ref<T[]>(toValue(options.forceRows) || []);

  if (options.forceRows) {
    watch(
      options.forceRows,
      (newRows) => {
        rows.value = toValue(newRows) || [];
      },
      { deep: true, immediate: true },
    );
  }

  const createQuery = () => {
    if (queryOptions.value !== undefined) {
      const queryFn = queryOptions.value.queryFn;
      return useQuery<T[], any>({
        initialData: [], // fallback / default value
        placeholderData: keepPreviousData,
        // @ts-ignore
        queryFn: async (ctx) => {
          try {
            if (queryFn) {
              // @ts-ignore
              rows.value = await queryFn(ctx);
            } else {
              rows.value = [];
            }

            return rows.value;
          } catch (e) {
            console.error(e);
            return [];
          }
        },
        queryKey: queryOptions.value.queryKey,
        refetchOnWindowFocus: (queryOptions.value.refetchOnWindowFocus ?? false) as
          | boolean
          | 'always'
          | undefined,
      });
    }
  };

  const query = ref(options.queryOptions ? createQuery() : toValue(options.query || { data: [] }));
  const queryParams = ref<Q>(toValue(options.params) || ({} as Q));

  const pagination = ref<PaginationState>(queryParams.value.paginationState);
  const sorting = ref<SortingState>(queryParams.value.sortState);
  const columnFilters = ref<ColumnFiltersState>([]);
  const rowSelection = ref<RowSelectionState>({});

  // Stable per-table key derived from all column accessor keys / ids
  const tableKey =
    options.tableOptions.columns
      ?.map((c: any) => c.accessorKey || c.id)
      .filter(Boolean)
      .join(',') || 'default';

  const readLocalStorage = <T>(key: string): T => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch {
      localStorage.removeItem(key);
      return {} as T;
    }
  };

  // Load persisted column sizing from localStorage
  const sizingStorageKey = `table-column-sizing-${tableKey}`;
  const columnSizing = ref<ColumnSizingState>(readLocalStorage(sizingStorageKey));

  // Load persisted column visibility from localStorage
  const visibilityStorageKey = `table-column-visibility-${tableKey}`;
  const columnVisibility = ref<VisibilityState>(readLocalStorage(visibilityStorageKey));

  const isInternalChange = ref<any>(null);

  watch(pagination, (updatedPagination: PaginationState) => {
    queryParams.value.state = {
      pagination: updatedPagination,
      sort: sorting.value,
    };
  });

  // watch(
  //   () => route.query,
  //   (query) => {
  //     const copy: PaginatedQueryParams = queryParams.value.clone<PaginatedQueryParams>();
  //     copy.update(query)
  //     const requiresRefresh = Object.keys(queryParams.value.serialize()).some(key => {
  //       return queryParams.value[key] !== copy[key]
  //     });
  //
  //     if (requiresRefresh) {
  //       isInternalChange.value = false;
  //       queryParams.value.update(query, true);
  //       sorting.value = copy.sortState;
  //       pagination.value = queryParams.value.paginationState;
  //       table.value = createTable();
  //     } else {
  //       isInternalChange.value = null;
  //     }
  //   },
  //   { deep: true }
  // )

  watch(
    queryParams,
    (params: PaginatedQueryParams) => {
      if (params.paginationState.pageIndex !== pagination.value.pageIndex) {
        pagination.value.pageIndex = params.paginationState.pageIndex;
      }

      if (options.useRouterQuery && isInternalChange.value !== false) {
        router.push({ query: params.serialize() });
      } else {
        isInternalChange.value = null;
      }
      options.onQueryChange && options.onQueryChange();
    },
    { deep: true },
  );

  const rowData = computed(() => {
    return toValue(options.forceRows)
      ? toValue(options.forceRows)
      : query.value
        ? query.value.data
        : ((rows.value || []) as T[]);
  });

  const createTable = (): TanstackTable<T> => {
    return useVueTable<T>({
      columnResizeMode: 'onChange',
      get data() {
        return options.forceRows ? toValue(options.forceRows) : (rowData.value as T[]);
      },
      defaultColumn: {
        size: Number.MAX_SAFE_INTEGER,
      },
      enableColumnResizing: true,
      getCoreRowModel: getCoreRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      manualPagination: true,
      manualSorting: true,
      onColumnFiltersChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnFilters),
      onColumnSizingChange: (updaterOrValue) => {
        valueUpdater(updaterOrValue, columnSizing);
        localStorage.setItem(sizingStorageKey, JSON.stringify(columnSizing.value));
      },
      onColumnVisibilityChange: (updaterOrValue) => {
        valueUpdater(updaterOrValue, columnVisibility);
        localStorage.setItem(visibilityStorageKey, JSON.stringify(columnVisibility.value));
      },
      onPaginationChange: (updatedOrValue) => {
        valueUpdater(updatedOrValue, pagination);
      },
      onRowSelectionChange: (updaterOrValue) => {
        valueUpdater(updaterOrValue, rowSelection);

        if (options.selectItemsWithIds) {
          options.selectItemsWithIds(Object.keys(rowSelection.value));
        }
      },
      onSortingChange: (updaterOrValue) => {
        let newSort =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(options.defaultSort ? [] : sorting.value)
            : updaterOrValue;

        // NOTE: if there is no newSort, however there is a default
        if (!newSort?.length && options.defaultSort) {
          // don't allow default sort to be unset
          if (sorting.value[0].id === options.defaultSort[0].id) {
            sorting.value[0].desc = !sorting.value[0].desc;
            newSort = sorting.value;
          }
          // fall back to the default sort
          else {
            newSort = options.defaultSort;
          }
        }

        sorting.value = newSort;
        pagination.value = {
          pageIndex: 0,
          pageSize: pagination.value.pageSize,
        };
      },
      state: {
        get columnFilters() {
          return columnFilters.value;
        },
        get columnSizing() {
          return columnSizing.value;
        },
        get columnVisibility() {
          return columnVisibility.value;
        },
        get pagination() {
          return pagination.value;
        },
        get rowSelection() {
          return rowSelection.value;
        },
        get sorting() {
          return sorting.value;
        },
      },
      ...options.tableOptions,
      // Re-expose columns as a live getter so reactive (e.g. flag-gated)
      // column sets stay up to date. Spreading tableOptions above would
      // otherwise evaluate the caller's `get columns()` once at setup.
      get columns() {
        return toValue(options.tableOptions.columns);
      },
    });
  };

  table.value = createTable();

  const removeRow = (row: Row<T>) => {
    if (table.value) {
      const idx = indexOf(table.value.getRowModel().rows, (r: Row<T>) => r.id === row.id);
      if (idx === -1) return;
      const rowsCopy = rows.value.slice();
      rowsCopy.splice(idx, 1);
      rows.value = rowsCopy;

      if (queryOptions.value) {
        queryClient.setQueryData(queryOptions.value.queryKey, (oldData: T[] | undefined) => {
          if (!oldData) return [];
          return rows.value;
        });
      }
    }
  };

  const updateRow = (row: Row<T>, updated: T) => {
    if (!table.value) return;

    table.value.getRow(row.id)._valuesCache = {};

    if (queryOptions.value) {
      const idx = indexOf(table.value.getRowModel().rows, (r: Row<T>) => r.id === row.id);
      if (idx === -1) return;
      const rowsCopy = rows.value.slice();
      // @ts-ignore
      rowsCopy[idx] = updated;
      rows.value = rowsCopy;

      queryClient.setQueryData(queryOptions.value.queryKey, (oldData: T[] | undefined) => {
        if (!oldData) return [];
        return rows.value;
      });
    }
  };

  return {
    removeRow,
    rows: rows as Ref<T[]>,
    table: table.value as TanstackTable<T>,
    updateRow,
  };
}

export function usePersistentSelection<T extends { id: string }>(
  getById: (id: string) => T | undefined,
) {
  const selectedItems = shallowRef<T[]>([]);

  const updateSelection = (ids: string[]) => {
    const currentIds = new Set(selectedItems.value.map((item) => item.id));
    const newIds = new Set(ids);

    // selecting items
    for (const id of ids) {
      if (!currentIds.has(id)) {
        const item = getById(id);
        if (item) {
          selectedItems.value.push(item);
        }
      }
    }

    // deselecting items
    selectedItems.value = selectedItems.value.filter((item) => newIds.has(item.id));
  };

  const clearSelection = () => {
    selectedItems.value = [];
  };

  return {
    clearSelection,
    selectedItems,
    updateSelection,
  };
}

export function getRowRange<T>(rows: Array<Row<T>>, idA: string, idB: string) {
  const range: Array<Row<T>> = [];
  let foundStart = false;
  let foundEnd = false;
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (row.id === idA || row.id === idB) {
      if (foundStart) {
        foundEnd = true;
      }
      if (!foundStart) {
        foundStart = true;
      }
    }

    if (foundStart) {
      range.push(row);
    }
    if (foundEnd) {
      break;
    }
  }
  if (!foundEnd) {
    throw Error('Could not find whole row range');
  }
  return range;
}
